import React from 'react';

import { Link } from 'react-router-dom';
import {
  Box,
  Chip,
  Divider,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Allow } from '../../../../components/auth/Allow';
import { JumpPersonaButton } from '../../../../components/personas/JumpPersonaButton';
import SimpleTooltip from '../../../../components/SimpleTooltip';
import { onJumpRequested } from '../../../../utils';

import { ClaimIndicator } from '../../../../_assets/svg/claims/ClaimIndicator.svg';
import { ProductTypes, isEndorsableProductType } from '../../../../types';
import {
  CANCELLED_IN_REINSTATEMENT_PERIOD,
  CANCELLED_PENDING_STATUS,
  CANCELLED_STATUS,
  IN_FORCE_STATUS,
  PolicyStatus,
} from '../../../_statics/policy.statics';
import { NoKnownLossLetterIndicator } from '../../../../components/Reusable';
import UWInboxAccNotes from '../../../../_assets/svg/UWInboxAccNotes.svg';
import UWInboxNotes from '../../../../_assets/svg/UWInboxNotes.svg';
import { Note } from '../../../../components/notes/notes.helper';
import PolicyDrawer from '../../drawers/policyholder/PolicyDrawer';
import { P100PolicyMidTermEndorsementDrawer } from '../../../_global/endorsements/P100PolicyMidTermEndorsementDrawer';
import { getPolicyDetails } from '../../../../api/policies.api';
import { PolicyDetailsDrawer } from '../../drawers/cowbell/policy-details-drawer/PolicyDetailsDrawer';
import AccountLink from '../../../../components/tables/cell-components/account-link/AccountLink';
import EyeButton from '../../../../components/Buttons/EyeButton';
import { BORIndicatorCell } from '../../../../components/tables/cell-components/BORIndicatorCell';
import { useChatContexts } from '../../../../components/ask-moo/hooks/useChatContexts';

import {
  AvatarWithInitialsFallBack,
  constructAvatarInitials,
  useProfilePhoto,
} from '../../../../components/notes/AvatarWithInitialsFallBack';
import { PERSONA_TYPES } from '../../../_statics/persona.statics';
import { usePersona } from '../../../../components/hooks/usePersona';
import SimpleDateDisplay from '../../../../components/SimpleDateDisplay';
import { fetchLatestPolicyData } from '../../../../policies/PolicyService';
import {
  FORMAT_DATE,
  FORMAT_DATE_AND_TIME,
  toUniversalUtcDate,
} from '../../../../utils/date.utils';
import { determineIsLegacyProduct } from '../../../agencies/quotes/PrimeX/utils/primeX.util';
import { PxMidTermEndorsementDrawer } from '../../../_global/endorsements/PxMidTermEndorsementDrawer';

const policyStatusForShowingDrawer = [
  PolicyStatus.ISSUED_STATUS,
  PolicyStatus.IN_FORCE_STATUS,
  PolicyStatus.RENEWED_STATUS,
  PolicyStatus.EXPIRED_STATUS,
];

export function PolicyRowDetail({ row, onExpandRow }: any) {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(true);
  const persona = usePersona();
  const { replaceEntityContext } = useChatContexts();

  const shouldShowDrawer =
    !row.isPrimePlus && !(row.isBOR && persona.isCluster);

  React.useEffect(() => {
    if (shouldShowDrawer && row?.id && row?.policyNumber) {
      replaceEntityContext('policy', row.id, {
        policyNumber: row.policyNumber,
        companyName: row.companyName,
        accountId: row.accountId,
        effectiveDate: row.effectiveDate,
        endDate: row.endDate,
        premium: row.premium || row.totalPremium,
        productType: row.product,
        status: row.status,
      });
    }
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shouldShowDrawer) {
    return null;
  }

  const fetchLatestPolicyByStatus = policyStatusForShowingDrawer.includes(
    row.status
  );

  if (row.isEndorsement) {
    return (
      <PolicyDrawer
        policyId={row.id}
        product={row.product}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          onExpandRow(row.id);
        }}
      >
        {({ policy = {} }) => {
          if (determineIsLegacyProduct(row?.product)) {
            return (
              <Box
                style={{
                  overflowY: 'auto',
                }}
              >
                <P100PolicyMidTermEndorsementDrawer
                  // @ts-expect-error TS(2339): Property 'coverages' does not exist on type '{}'.
                  policy={{ ...policy, ...policy.coverages }}
                />
              </Box>
            );
          }

          return (
            <Box
              style={{
                overflow: 'auto',
                overflowX: 'hidden',
                maxWidth: '70rem',
              }}
            >
              <PxMidTermEndorsementDrawer
                endorsement={{
                  // @ts-expect-error TS(2339): Property 'policy' does not exist on type '{}'.
                  ...policy.policy,
                  // @ts-expect-error TS(2339): Property 'policy' does not exist on type '{}'.
                  aggLimit: policy.policy.limit,
                  // @ts-expect-error TS(2339): Property 'firmographicData' does not exist on type... Remove this comment to see the full error message
                  firmographicData: policy.firmographicData,
                }}
              />
            </Box>
          );
        }}
      </PolicyDrawer>
    );
  }

  return (
    <PolicyDrawer
      policyId={row.id}
      product={row.product}
      policyNumber={row.policyNumber}
      fetchLatestPolicyByStatus={fetchLatestPolicyByStatus}
      open={isDrawerOpen}
      onClose={() => {
        setIsDrawerOpen(false);
        onExpandRow(row.id);
      }}
      onFetch={onFetch}
    >
      {({ policy }: any) => {
        return (
          <Box
            style={{
              overflowY: 'auto',
              maxWidth: '45rem',
            }}
          >
            <PolicyDetailsDrawer
              policy={policy}
              getPolicyStatus={getPolicyStatus}
              product={row.product}
              fetchLatestPolicyByStatus={fetchLatestPolicyByStatus}
            />
          </Box>
        );
      }}
    </PolicyDrawer>
  );
}

const onFetch = (
  policyId: any,
  product: any,
  policyNumber: any,
  fetchLatestPolicyByStatus: any
) => {
  if (isEndorsableProductType(product) && fetchLatestPolicyByStatus) {
    return Promise.all([
      getPolicyDetails(policyId, product),
      fetchLatestPolicyData({ product, policyNumber }),
    ]).then(([originalPolicy, latestPolicy]) => {
      return {
        originalPolicy: originalPolicy?.data?.data,
        latestPolicy: latestPolicy?.data,
      };
    });
  }

  return getPolicyDetails(policyId, product).then(({ data }) => data?.data);
};

export const ClaimTag = styled(ClaimIndicator)({
  position: 'absolute',
  bottom: '1rem',
});

export const cowbellPolicyDatePresets = [
  {
    label: '7 days',
    value: '1',
  },
  {
    label: '1 month',
    value: '2',
  },
  {
    label: '3 months',
    value: '3',
  },
  {
    label: '6 months',
    value: '4',
  },
  {
    label: '1 year',
    value: '5',
  },
  {
    label: '2 years',
    value: '11',
  },
  {
    label: '3 years',
    value: '12',
  },
];

export const AgencyNameCell = ({ policy = {} }) => {
  // @ts-expect-error TS(2339): Property 'isBOR' does not exist on type '{}'.
  const hasProcessedBOR = policy.isBOR && policy.brokerOfRecord;
  const currentAgency = hasProcessedBOR
    ? {
        // @ts-expect-error TS(2339): Property 'brokerOfRecord' does not exist on type '... Remove this comment to see the full error message
        agencyId: policy.brokerOfRecord.agencyId,
        // @ts-expect-error TS(2339): Property 'brokerOfRecord' does not exist on type '... Remove this comment to see the full error message
        agencyName: policy.brokerOfRecord.agencyName,
      }
    : // @ts-expect-error TS(2339): Property 'agencyId' does not exist on type '{}'.
      { agencyId: policy.agencyId, agencyName: policy.agencyName };

  return (
    <BORIndicatorCell policy={policy}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between' }}
        className="expand-icon"
      >
        <Box
          style={{
            maxWidth: '14rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Link
            style={{ color: 'inherit' }}
            to={`/admin/agencies?search=${currentAgency.agencyId}&allMarkets=1`}
            onClick={(event) => event.stopPropagation()}
          >
            {currentAgency.agencyName}
          </Link>
        </Box>
        {/* jump needs to be on the processed BOR agency if applicable */}
        <Allow allow={['agencies:enter', 'account:cowbell']}>
          <JumpPersonaButton
            onJumpRequest={onJumpRequested(
              PERSONA_TYPES.AGENCY,
              currentAgency.agencyId,
              currentAgency.agencyName
            )}
          />
        </Allow>
      </div>
    </BORIndicatorCell>
  );
};

const getAgentNameDisplayValue = (
  agentFirstName?: string,
  agentLastName?: string
) => {
  const fullName = `${agentFirstName ?? ''} ${agentLastName ?? ''}`.trim();
  return fullName.length ? fullName : '-';
};

const getAgentEmailDisplayValue = (agentEmail?: string) => {
  return agentEmail || '-';
};

const getBorIndicatorColor = (policy: any, theme: any) => {
  return policy.isBOR
    ? theme.config.colors.cowbellBlue
    : theme.palette.error.light;
};

const BorTooltipLineItem = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Box display="flex" justifyContent="space-between">
    <Box width="12rem">
      <Typography variant="body2">{label}</Typography>
    </Box>
    <Box width="15rem" overflow="hidden">
      <Typography variant="body2" noWrap>
        {value}
      </Typography>
    </Box>
  </Box>
);

const AgentNameTooltipContent = ({
  oldAgentName,
  newAgentName,
  isBOR,
}: {
  oldAgentName: string;
  newAgentName: string;
  isBOR: boolean;
}) => (
  <Box>
    <Typography align="center" variant="subtitle2">
      {isBOR ? 'BOR Processed' : 'BOR in Process'}
    </Typography>
    <Box marginY="1rem">
      <Divider />
    </Box>
    <BorTooltipLineItem label="Old Agent:" value={oldAgentName} />
    <Box marginY="1rem">
      <Divider />
    </Box>
    <BorTooltipLineItem label="New Agent:" value={newAgentName} />
  </Box>
);

const AgentEmailTooltipContent = ({
  oldEmail,
  newEmail,
  isBOR,
}: {
  oldEmail: string;
  newEmail: string;
  isBOR: boolean;
}) => (
  <Box>
    <Typography align="center" variant="subtitle2">
      {isBOR ? 'BOR Processed' : 'BOR in Process'}
    </Typography>
    <Box marginY="1rem">
      <Divider />
    </Box>
    <BorTooltipLineItem label="Old Email:" value={oldEmail} />
    <Box marginY="1rem">
      <Divider />
    </Box>
    <BorTooltipLineItem label="New Email:" value={newEmail} />
  </Box>
);

const AgentCellWithTooltip = ({
  displayValue,
  tooltipContent,
  highlightColor,
}: {
  displayValue: string;
  tooltipContent: React.ReactNode;
  highlightColor: string;
}) => (
  <SimpleTooltip title={tooltipContent}>
    <span style={{ color: highlightColor, fontWeight: 'bold' }}>
      {displayValue}
    </span>
  </SimpleTooltip>
);

export const AgentNameWithBorTooltipCell = ({ policy = {} }: any) => {
  const theme = useTheme();

  const oldAgentName = getAgentNameDisplayValue(
    policy.agentFirstName,
    policy.agentLastName
  );

  if (!policy.brokerOfRecord) {
    return oldAgentName;
  }

  const newAgentName = getAgentNameDisplayValue(
    policy.brokerOfRecord.agentFirstName,
    policy.brokerOfRecord.agentLastName
  );

  // When BOR is processed, show NEW agent; otherwise show OLD agent
  const hasProcessedBOR = policy.isBOR && policy.brokerOfRecord;
  const displayValue = hasProcessedBOR ? newAgentName : oldAgentName;

  const highlightColor = getBorIndicatorColor(policy, theme);

  return (
    <AgentCellWithTooltip
      displayValue={displayValue}
      tooltipContent={
        <AgentNameTooltipContent
          oldAgentName={oldAgentName}
          newAgentName={newAgentName}
          isBOR={policy.isBOR}
        />
      }
      highlightColor={highlightColor}
    />
  );
};

export const AgentEmailWithBorTooltipCell = ({ policy = {} }: any) => {
  const theme = useTheme();

  const oldAgentEmail = getAgentEmailDisplayValue(policy.agentEmail);

  if (!policy.brokerOfRecord) {
    return oldAgentEmail;
  }

  const newAgentEmail = getAgentEmailDisplayValue(
    policy.brokerOfRecord.agentEmail
  );

  // When BOR is processed, show NEW email; otherwise show OLD email
  const hasProcessedBOR = policy.isBOR && policy.brokerOfRecord;
  const displayValue = hasProcessedBOR ? newAgentEmail : oldAgentEmail;

  const highlightColor = getBorIndicatorColor(policy, theme);

  return (
    <AgentCellWithTooltip
      displayValue={displayValue}
      tooltipContent={
        <AgentEmailTooltipContent
          oldEmail={oldAgentEmail}
          newEmail={newAgentEmail}
          isBOR={policy.isBOR}
        />
      }
      highlightColor={highlightColor}
    />
  );
};

export const renderDateComponent = (date: any) => (
  <SimpleDateDisplay date={date} />
);
export const derivePolicyEffectiveDate = (
  policy = {},
  render = (x: any) => x
) => {
  const invalidStatus = ['DELETED'];
  // @ts-expect-error TS(2339): Property 'customerStatus' does not exist on type '... Remove this comment to see the full error message
  if (invalidStatus.includes(policy.customerStatus)) {
    return '-';
  }

  // @ts-expect-error TS(2339): Property 'effectiveDate' does not exist on type '{... Remove this comment to see the full error message
  return policy.effectiveDate ? render(policy.effectiveDate) : '-';
};

export const derivePolicyEndDate = (policy = {}, render = (x: any) => x) => {
  const invalidStatus = ['DELETED'];
  // @ts-expect-error TS(2339): Property 'customerStatus' does not exist on type '... Remove this comment to see the full error message
  if (invalidStatus.includes(policy.customerStatus)) {
    return '-';
  }

  // @ts-expect-error TS(2339): Property 'endDate' does not exist on type '{}'.
  return policy.endDate ? render(policy.endDate) : '-';
};

export function formatUTCDate(dateString: any, shouldDisplayTime = false) {
  return toUniversalUtcDate(dateString, {
    format: shouldDisplayTime ? FORMAT_DATE_AND_TIME : FORMAT_DATE,
  });
}

export function PolicyNumberCell({ row }: any) {
  const theme = useTheme();

  const claimStatus = row.claimStatus ?? '';
  const tagcolor =
    row.claimStatus === 'CLOSED' ? theme.config.colors.cowbellAccent : 'salmon';

  return (
    <Box style={{ position: 'relative' }}>
      {row.claimId && (
        <SimpleTooltip
          title={`${claimStatus} - Claim Number ${row.claimNumber}`}
        >
          <ClaimTag color={tagcolor} />
        </SimpleTooltip>
      )}
      {row.policyNumber}
    </Box>
  );
}

export function getAgentCellValue(row: any) {
  // When BOR is processed, export NEW agent; otherwise export OLD agent
  const hasProcessedBOR = row.isBOR && row.brokerOfRecord;

  const agentFirstName = hasProcessedBOR
    ? row.brokerOfRecord.agentFirstName
    : row.agentFirstName;
  const agentLastName = hasProcessedBOR
    ? row.brokerOfRecord.agentLastName
    : row.agentLastName;

  // Use same logic as getAgentNameDisplayValue to avoid "undefined" in output
  const fullName = `${agentFirstName ?? ''} ${agentLastName ?? ''}`.trim();
  return fullName.length ? fullName : '-';
}

export function getStatusCellValue(row: any) {
  const { noKnownLossLetterReq } = row;

  const status = getPolicyStatus(row);

  return (
    <div style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}>
      {status}
      <NoKnownLossLetterIndicator status={noKnownLossLetterReq} />
    </div>
  );
}

export function getNotesCellValue(row: any) {
  const attachmentMetaData = {
    accountId: row.accountId,
    agencyId: row.agencyId,
    companyName: row.companyName,
  };

  return (
    <div style={{ display: 'flex' }}>
      <SimpleTooltip title="Account Notes">
        <UWInboxAccNotes
          onClick={(event: any) => {
            event.stopPropagation();
            Note.show('account', {
              data: {
                accountId: row.accountId,
                attachmentMetaData,
              },
            });
          }}
        />
      </SimpleTooltip>

      {row.quoteId && (
        <SimpleTooltip title="Quote Notes">
          <UWInboxNotes
            onClick={(event: any) => {
              event.stopPropagation();
              Note.show('quote', {
                data: {
                  quoteId: row.quoteId,
                  product: row.product,
                },
              });
            }}
          />
        </SimpleTooltip>
      )}
    </div>
  );
}

export function getCompanyNameCellValue(row: any, context: any) {
  const { onOpenDrawer } = context;

  const handleOpenDrawer = (e: any) => {
    e.stopPropagation();
    onOpenDrawer({ id: row.accountId, name: row.companyName });
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box
        style={{
          maxWidth: '14rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <AccountLink accountId={row.accountId} account={row}>
          {row.companyName || '-'}
        </AccountLink>
      </Box>

      <EyeButton onClick={handleOpenDrawer} />
    </div>
  );
}

const chipStyling = (row: any, config: any) => {
  if (!row.isBOR && row.brokerOfRecord) {
    return {
      color: config.colors.error,
      icon: <CloseIcon />,
    };
  }
  return {
    color: config.colors.cowbellGreen,
    icon: <DoneIcon />,
  };
};

export const BORChip = ({ row }: any) => {
  const theme = useTheme();

  const chipConfig = chipStyling(row, theme.config);

  if (!row?.isBOR && !row?.brokerOfRecord) {
    return null;
  }

  return (
    <Chip
      style={{
        border: `1px solid ${chipConfig.color}`,
        pointerEvents: 'none',
      }}
      icon={chipConfig.icon}
      label="BOR"
      size="small"
    />
  );
};

export const APIChip = ({ row }: any) => {
  const theme = useTheme();
  const chipConfig = {
    color: theme.config.colors.cowbellGreen,
    icon: <DoneIcon />,
  };

  if (!row?.isAPI) {
    return <>-</>;
  }

  return (
    <Chip
      style={{
        border: `1px solid ${chipConfig.color}`,
        pointerEvents: 'none',
      }}
      icon={chipConfig.icon}
      label="API"
      size="small"
    />
  );
};

export const AgentNameCell = ({ row }: any) => {
  const { data: profilePhoto, isInitialLoading: isLoading } = useProfilePhoto({
    email: row.agentEmail,
  });

  if (isLoading) {
    return null;
  }
  if (!row.agentFirstName || !row.agentLastName) {
    return '';
  }

  const agentFullName = `${row.agentFirstName} ${row.agentLastName}`;
  const agentInitials = constructAvatarInitials(agentFullName);

  return (
    <Box display="flex" alignItems="center">
      <Box style={{ height: '2.5rem', width: '2.5rem', margin: '0.3rem' }}>
        <AvatarWithInitialsFallBack
          showProfilePhoto={!!profilePhoto}
          profilePhoto={profilePhoto}
          name={agentFullName}
          initials={agentInitials}
        />
      </Box>
      <div
        style={{
          marginLeft: '0.5rem',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.agentFirstName} {row.agentLastName}
      </div>
    </Box>
  );
};

export const getPolicyStatus = (policy: any) => {
  let { status } = policy;

  const isInReinstatementPeriod = status === CANCELLED_IN_REINSTATEMENT_PERIOD;
  const isCancelPending = status === CANCELLED_PENDING_STATUS;

  // Legacy (P250-1540) surfaces a NOC-sent cancel-pending policy as In Force. For Prime X, business +
  // legal confirmed the UI should keep showing Cancel Pending (BE and FE consistent), so raterX is
  // excluded here. FPX-5538.
  if (
    isCancelPending &&
    policy.isNocSent &&
    policy.product !== ProductTypes.p100 &&
    !policy.raterXCompatible
  ) {
    status = IN_FORCE_STATUS;
  }

  if (isInReinstatementPeriod) {
    status = CANCELLED_STATUS;
  }

  return status;
};
